import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from './smart-review-api.service';
import {
  GitBranch,
  GitCommit,
  GitFileMatch,
  GitFileRevision,
  GitProject,
  GitProvider,
  GitPullRequest,
  GitPullState,
  GitRepoQuery,
  GitRepository,
  ReviewableRepositories,
  SmartReviewCapabilities
} from '../interfaces/git.interface';

/**
 * Integração git do Smart Review: navegação por demanda até o Pull Request.
 *
 * A jornada de review usa o cadastro local para decidir o que a ferramenta suporta; as
 * listagens do provedor continuam existindo para navegação por repositório e cadastro,
 * sempre no escopo do usuário autenticado.
 *
 * Nenhum método aqui faz `catchError(mainService.handleError)`, pelo mesmo motivo do
 * CostsService e do GatesService: aquele handler troca o `HttpErrorResponse` por um `Error`
 * genérico, sem `status` e sem corpo. Com GitHub e Bitbucket validando o acesso de cada
 * pessoa, "sem permissão no repositório" (403) e "credencial do provedor mal configurada"
 * (503) passaram a ser respostas comuns destas listagens — e a tela precisa da `message`
 * do backend para dizê-las, e do `status` para o cadastro tratar o 403. O erro sobe cru.
 */
@Injectable()
export class GitService extends SmartReviewApiService {
  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  /**
   * Diz de quais origens o usuário pode pedir review.
   *
   * É o que decide quais ramos do "Novo Review" existem. Antes a tela descobria isso
   * sondando a listagem de reviews, que passa pelo gate do TFVC — e quem só trabalha em
   * repositório git era barrado da ferramenta inteira.
   */
  getCapabilities(): Observable<SmartReviewCapabilities> {
    return this.http
      .get<SmartReviewCapabilities>(`${this.apiURL}/me/capabilities`, { headers: this.jsonHeaders })
      .pipe(
        map(res => ({
          user: res?.user ?? '',
          areaId: res?.areaId,
          capabilities: Array.isArray(res?.capabilities) ? res.capabilities : [],
          sources: {
            tfvc: res?.sources?.tfvc === true,
            git: res?.sources?.git === true,
            // Ausente vira true: backend anterior não manda o campo, e tratar a omissão
            // como "não há cadastro" bloquearia a origem git por falta de informação.
            gitHasRepositories: res?.sources?.gitHasRepositories !== false
          }
        }))
      );
  }

  /** Provedores configurados. Só os `browsable` devem ser oferecidos na navegação. */
  listProviders(): Observable<GitProvider[]> {
    return this.http
      .get<{ providers?: GitProvider[] }>(`${this.apiURL}/git/providers`, { headers: this.jsonHeaders })
      .pipe(
        map(res => (res?.providers ?? []).map(p => ({
          provider: p?.provider ?? '',
          browsable: p?.browsable === true,
          projectScoped: p?.projectScoped === true,
          // Booleano só quando o backend responde: `undefined` é "não sei" e o passo
          // oferece o provedor. Normalizar para `false` bloquearia todo provedor num
          // backend que não manda o campo; para `true`, esconderia o cadastro ausente.
          hasRepositories: typeof p?.hasRepositories === 'boolean' ? p.hasRepositories : undefined
        })).filter(p => p.provider !== ''))
      );
  }

  /**
   * Projetos do provedor, para o cadastro. Usado por Azure DevOps antes do repositório.
   *
   * A lista é a da credencial de serviço do Smart Review, e não a do admin logado: quem
   * cadastra decide o que a ferramenta revisa, e o token costuma alcançar mais projetos do
   * que qualquer pessoa. A rota é admin-only por isso.
   */
  listProjects(provider: string, query = '', limit = 50): Observable<GitProject[]> {
    let params = new HttpParams()
      .set('provider', provider)
      .set('limit', String(limit));

    if (query.trim()) {
      params = params.set('q', query.trim());
    }

    return this.http
      .get<{ projects?: any[] }>(`${this.apiURL}/admin/git/projects`, { headers: this.jsonHeaders, params })
      .pipe(
        map(res => (res?.projects ?? []).map((p): GitProject => ({
          id: p?.id || undefined,
          name: p?.name ?? '',
          description: p?.description || undefined,
          webUrl: p?.webUrl || undefined
        })).filter(p => !!p.name))
      );
  }

  /** Repositórios do provedor, para o cadastro — escopo da credencial de serviço, admin-only. `query` filtra por nome. */
  listRepositories(provider: string, query = '', limit = 30, project = ''): Observable<GitRepository[]> {
    let params = new HttpParams()
      .set('provider', provider)
      .set('limit', String(limit));

    if (project.trim()) {
      params = params.set('project', project.trim());
    }

    if (query.trim()) {
      params = params.set('q', query.trim());
    }

    return this.http
      .get<{ repositories?: any[] }>(`${this.apiURL}/admin/git/repositories`, { headers: this.jsonHeaders, params })
      .pipe(
        map(res => (res?.repositories ?? []).map((r): GitRepository => ({
          owner: r?.owner ?? '',
          project: r?.project || undefined,
          externalId: r?.externalId || undefined,
          name: r?.name ?? '',
          fullName: r?.fullName ?? [r?.owner, r?.project, r?.name].filter(Boolean).join('/'),
          description: r?.description || undefined,
          defaultBranch: r?.defaultBranch || undefined,
          private: r?.private === true,
          webUrl: r?.webUrl || undefined,
          slug: r?.slug ?? '',
          registered: r?.registered === true
        })))
      );
  }

  /**
   * Repositórios que o usuário pode mandar revisar: os cadastrados no Smart Review,
   * habilitados e acessíveis para ele no provedor Git.
   *
   * É outra lista, e não um filtro da anterior. `/admin/git/repositories` mostra o que existe
   * no provedor — centenas, a maioria em linguagem que a ferramenta não revisa — e serve
   * à tela de cadastro. Esta responde "o que dá para revisar", que é a pergunta da
   * jornada de review.
   *
   * `query` vai para o servidor, não é filtro local: cada repositório devolvido custa
   * uma consulta de permissão ao provedor Git, então quem recorta é quem tem o cadastro.
   * Por isso a resposta traz `truncated` — a lista pode ser só a primeira página.
   */
  listReviewableRepositories(query = '', limit = 0): Observable<ReviewableRepositories> {
    let params = new HttpParams();
    if (query.trim()) {
      params = params.set('q', query.trim());
    }
    if (limit > 0) {
      params = params.set('limit', String(limit));
    }

    return this.http
      .get<{ repositories?: any[]; truncated?: boolean }>(`${this.apiURL}/repositories`, { headers: this.jsonHeaders, params })
      .pipe(
        map(res => ({
          truncated: res?.truncated === true,
          repositories: (res?.repositories ?? []).map((r): GitRepository => ({
            owner: r?.org ?? '',
            project: r?.project || undefined,
            externalId: r?.externalId || undefined,
            name: r?.name ?? '',
            fullName: [r?.org, r?.project, r?.name].filter(Boolean).join('/'),
            defaultBranch: r?.defaultBranch || undefined,
            private: false,
            slug: r?.slug ?? '',
            provider: r?.provider ?? '',
            // Vem do cadastro: por definição, todos aqui estão cadastrados.
            registered: true
          }))
        }))
      );
  }

  /** Pull Requests do repositório, no escopo do usuário. */
  listPullRequests(repo: GitRepoQuery, state: GitPullState = 'open', targetBranch = '', limit = 30): Observable<GitPullRequest[]> {
    let params = this.repoParams(repo)
      .set('state', state)
      .set('limit', String(limit));
    // O recorte por branch de destino vai ao backend, e não sobre a lista: ela vem
    // limitada aos mais recentes, e filtrar depois esconderia os que ficaram além.
    if (targetBranch) {
      params = params.set('targetBranch', targetBranch);
    }

    return this.http
      .get<{ pullRequests?: any[] }>(`${this.apiURL}/git/pulls`, { headers: this.jsonHeaders, params })
      .pipe(
        map(res => (res?.pullRequests ?? []).map((p): GitPullRequest => ({
          number: Number(p?.number) || 0,
          title: p?.title ?? '',
          state: p?.state ?? '',
          author: p?.author || undefined,
          sourceBranch: p?.sourceBranch || undefined,
          targetBranch: p?.targetBranch || undefined,
          webUrl: p?.webUrl || undefined,
          updatedAt: p?.updatedAt || undefined
        })).filter(p => p.number > 0))
      );
  }

  /**
   * Branches do repositório, da mais recentemente tocada para a mais antiga.
   *
   * A escolha existe porque o commit a revisar quase nunca está na branch padrão: quem
   * vai revisar acabou de empurrar para a sua própria.
   */
  listBranches(repo: GitRepoQuery, limit = 50): Observable<GitBranch[]> {
    return this.http
      .get<{ branches?: any[] }>(`${this.apiURL}/git/branches`, {
        headers: this.jsonHeaders,
        params: this.repoParams(repo).set('limit', String(limit))
      })
      .pipe(
        map(res => (res?.branches ?? []).map((b): GitBranch => ({
          name: b?.name ?? '',
          revision: b?.revision ?? '',
          committedAt: b?.committedAt || undefined
        })).filter(b => !!b.name))
      );
  }

  /** Commits de um ref. `ref` vazio usa a branch padrão do repositório. */
  listCommits(repo: GitRepoQuery, ref = '', limit = 30): Observable<GitCommit[]> {
    let params = this.repoParams(repo).set('limit', String(limit));
    if (ref.trim()) {
      params = params.set('ref', ref.trim());
    }

    return this.http
      .get<{ commits?: any[] }>(`${this.apiURL}/git/commits`, { headers: this.jsonHeaders, params })
      .pipe(
        map(res => (res?.commits ?? []).map((c): GitCommit => ({
          revision: c?.revision ?? '',
          shortRevision: c?.shortRevision ?? '',
          subject: c?.subject ?? '',
          author: c?.author || undefined,
          isMerge: c?.isMerge === true,
          committedAt: c?.committedAt || undefined
        })).filter(c => !!c.revision))
      );
  }

  /** Revisões que tocaram um arquivo cadastrado no repositório. */
  /**
   * Acha arquivos do repositório pelo NOME, sem diferenciar maiúsculas de minúsculas.
   *
   * Quem pede o review sabe `CRMA010.PRW`, não o diretório em que o fonte foi parar —
   * exigir o caminho completo fazia a tela falhar num nome que existe. O `path` devolvido
   * é o exato do repositório, e é ELE que deve ser reenviado nas chamadas seguintes: em
   * git dois arquivos podem diferir só na caixa, e reenviar o que foi digitado levaria o
   * review a ler o arquivo errado.
   */
  searchFiles(repoSlug: string, query: string, ref = '', limit = 50): Observable<GitFileMatch[]> {
    let params = new HttpParams()
      .set('q', query.trim())
      .set('limit', String(limit));

    if (ref.trim()) {
      params = params.set('ref', ref.trim());
    }

    return this.http
      .get<{ files?: GitFileMatch[] }>(
        `${this.apiURL}/repos/${encodeURIComponent(repoSlug)}/files`,
        { headers: this.jsonHeaders, params }
      )
      .pipe(
        map(res => Array.isArray(res?.files) ? res.files : [])
      );
  }

  listFileHistory(repoSlug: string, path: string, ref = '', limit = 30): Observable<GitFileRevision[]> {
    let params = new HttpParams()
      .set('path', path.trim().replace(/^\/+/, ''))
      .set('limit', String(limit));

    if (ref.trim()) {
      params = params.set('ref', ref.trim());
    }

    return this.http
      .get<{ revisions?: any[] }>(
        `${this.apiURL}/repos/${encodeURIComponent(repoSlug)}/files/history`,
        { headers: this.jsonHeaders, params }
      )
      .pipe(
        map(res => (res?.revisions ?? []).map((r): GitFileRevision => ({
          revision: r?.revision ?? '',
          shortRevision: r?.shortRevision ?? '',
          message: r?.message ?? '',
          authorName: r?.authorName || undefined,
          authorEmail: r?.authorEmail || undefined,
          committedAt: r?.committedAt || undefined
        })).filter(r => !!r.revision))
      );
  }

  /**
   * Parâmetros que identificam o repositório nas três listagens por repositório.
   *
   * `project` só acompanha quem tem: em Gitea e GitHub o campo não existe, e mandá-lo
   * vazio colocaria um parâmetro sem valor na query.
   */
  private repoParams(repo: GitRepoQuery): HttpParams {
    let params = new HttpParams()
      .set('provider', repo.provider)
      .set('owner', repo.owner)
      .set('name', repo.name);
    if (repo.project?.trim()) {
      params = params.set('project', repo.project.trim());
    }
    if (repo.externalId?.trim()) {
      params = params.set('externalId', repo.externalId.trim());
    }
    return params;
  }
}
